<template>
    <div class="ui-page">
        <ui-page-header :title="request.protocol || 'CHC'" :subtitle="request.id ? `${request.client_name || ''} · enviada em ${formatDateTime(request.created_at)}` : ''"
            back="/chc" back-text="Certidões de crédito (CHC)">
            <template v-if="request.id">
                <ui-badge :text="nameOf(statuses, request.status)" :tone="toneOf(statuses, request.status)" class="mr-3" />
                <v-btn outlined color="primary" class="mr-2" :loading="loading" :disabled="saving" @click="load">
                    <v-icon left small>mdi-refresh</v-icon>
                    Atualizar
                </v-btn>
                <template v-if="isOpen && $allowed('edit')">
                    <v-btn v-if="!editing" outlined color="primary" @click="startEditing">
                        <v-icon left small>mdi-pencil-outline</v-icon>
                        Revisar dados
                    </v-btn>
                    <template v-else>
                        <v-btn text class="mr-2" :disabled="saving" @click="cancelEditing">Cancelar</v-btn>
                        <v-btn color="primary" depressed :loading="saving" @click="saveReview">Salvar revisão</v-btn>
                    </template>
                </template>
            </template>
        </ui-page-header>

        <v-progress-linear v-if="loading && !request.id" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <template v-if="request.id">
            <!-- Situacao -->
            <v-alert v-if="request.status === 'documents_requested'" dense text type="info" class="mb-4">
                Documentos solicitados ao requerente em {{ formatDateTime(request.analyzed_at) }}<span v-if="request.analyzed_by_name"> por {{ request.analyzed_by_name }}</span>:
                <strong>{{ request.requested_documents.map((kind) => nameOf(documentKinds, kind)).join(', ') }}</strong>.
                <div v-if="request.decision_reason" class="chc-pre mt-1">{{ request.decision_reason }}</div>
            </v-alert>
            <v-alert v-else-if="request.status === 'documents_updated'" dense text type="success" class="mb-4">
                O requerente enviou os documentos solicitados. Confira na seção Documentos e retome a análise.
            </v-alert>
            <v-alert v-else-if="['approved', 'rejected', 'outdated'].includes(request.status)" dense text
                :type="request.status === 'approved' ? 'success' : (request.status === 'rejected' ? 'error' : 'warning')" class="mb-4">
                {{ nameOf(statuses, request.status) }} em {{ formatDateTime(request.analyzed_at) }}<span v-if="request.analyzed_by_name"> por {{ request.analyzed_by_name }}</span>.
                <span v-if="request.status === 'approved'">Créditos lançados no QGC vigente do caso.</span>
                <div v-if="request.decision_reason" class="chc-pre mt-1">{{ request.decision_reason }}</div>
            </v-alert>

            <div class="chc-overview">
                <div class="chc-overview__item"><span class="chc-label">Caso</span><span class="chc-value">{{ request.client_name || '—' }}</span></div>
                <div class="chc-overview__item">
                    <span class="chc-label">Requerente ({{ nameOf(requesterTypes, request.requester_type) }})</span>
                    <span class="chc-value">{{ request.requester_name || '—' }}</span>
                    <span class="chc-sub">{{ formatRegistration(request.requester_registration) }}<span v-if="request.requester_email"> · {{ request.requester_email }}</span></span>
                </div>
                <div class="chc-overview__item"><span class="chc-label">Total da certidão (R$)</span><span class="chc-value">{{ formatMoney(totalBrl) }}</span></div>
                <div class="chc-overview__item"><span class="chc-label">QGC vigente</span><span class="chc-value">{{ request.qgc_version ? `Versão ${request.qgc_version}` : 'Sem QGC importado' }}</span></div>
            </div>

            <!-- Processo de origem -->
            <ui-section title="Processo de origem">
                <div v-if="!editing" class="chc-grid">
                    <div><span class="chc-label">Número</span>{{ request.process_number }}</div>
                    <div><span class="chc-label">UF</span>{{ request.process_state || '—' }}</div>
                    <div><span class="chc-label">Ramo da justiça</span>{{ request.justice_branch ? nameOf(justiceBranches, request.justice_branch) : '—' }}</div>
                    <div><span class="chc-label">Órgão julgador</span>{{ request.court || '—' }}</div>
                    <div><span class="chc-label">Trânsito em julgado</span>{{ formatDate(request.final_judgment_at) || '—' }}</div>
                    <div><span class="chc-label">Cálculo atualizado em</span>{{ formatDate(request.calculation_updated_at) || '—' }}</div>
                    <div><span class="chc-label">Confidencial</span>{{ request.confidential ? 'Sim (segredo de justiça)' : 'Não' }}</div>
                    <div class="chc-grid__wide"><span class="chc-label">Observações</span><span class="chc-pre">{{ request.notes || '—' }}</span></div>
                </div>
                <v-row v-else dense>
                    <v-col cols="12" md="5"><v-text-field v-model="form.process_number" label="Número do processo" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="4" md="2"><v-select v-model="form.process_state" :items="states" label="UF" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="8" md="5"><v-select v-model="form.justice_branch" :items="justiceBranches" item-value="id" item-text="name" label="Ramo da justiça" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="12" md="6"><v-text-field v-model="form.court" label="Órgão julgador" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="6" md="3"><v-text-field v-model="form.final_judgment_at" type="date" label="Trânsito em julgado" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="6" md="3"><v-text-field v-model="form.calculation_updated_at" type="date" label="Cálculo atualizado em" outlined dense hide-details="auto" /></v-col>
                    <v-col cols="12"><v-textarea v-model="form.notes" label="Observações" rows="2" auto-grow outlined dense hide-details="auto" /></v-col>
                    <v-col cols="12"><v-checkbox v-model="form.confidential" label="Confidencial (segredo de justiça)" dense hide-details class="mt-0" /></v-col>
                </v-row>
            </ui-section>

            <!-- Credores -->
            <ui-section title="Credores" description="O alerta indica credor que já tem crédito no QGC vigente do caso.">
                <template v-if="editing" #actions>
                    <v-btn outlined small color="primary" @click="form.creditors.push({ key: newKey(), name: '', registration: '' })"><v-icon left small>mdi-plus</v-icon>Credor</v-btn>
                </template>
                <v-simple-table dense>
                    <thead><tr><th>Nome</th><th>CPF/CNPJ</th><th>Alerta</th><th v-if="editing"></th></tr></thead>
                    <tbody v-if="!editing">
                        <tr v-for="creditor in request.creditors" :key="creditor.id">
                            <td>{{ creditor.name }}</td>
                            <td>{{ formatRegistration(creditor.registration) }}</td>
                            <td>
                                <span v-if="creditor.qgc_claims && creditor.qgc_claims.length" class="chc-warn">
                                    <v-icon x-small color="#b54708">mdi-alert-outline</v-icon>
                                    Já está no QGC: {{ creditor.qgc_claims.map((claim) => `${className(claim.type)} · ${formatMoney(claim.amount)}`).join('; ') }}
                                </span>
                                <span v-else class="ui-muted">—</span>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-for="(creditor, index) in form.creditors" :key="creditor.key">
                            <td><v-text-field v-model="creditor.name" dense hide-details outlined class="chc-input" /></td>
                            <td><v-text-field v-model="creditor.registration" dense hide-details outlined class="chc-input" /></td>
                            <td class="ui-muted">—</td>
                            <td class="text-right"><v-btn icon x-small :disabled="form.creditors.length === 1" @click="removeCreditor(index)"><v-icon small>mdi-delete-outline</v-icon></v-btn></td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </ui-section>

            <!-- Creditos -->
            <ui-section title="Créditos" description="A classe revisada é a que vai para o QGC na aprovação; sem revisão, vale a informada.">
                <template v-if="editing" #actions>
                    <v-btn outlined small color="primary" @click="addCredit"><v-icon left small>mdi-plus</v-icon>Crédito</v-btn>
                </template>
                <v-simple-table v-if="!editing" dense class="chc-table">
                    <thead><tr><th>Credor</th><th>Devedora</th><th>Origem</th><th class="text-right">Valor</th><th>Classe informada</th><th>Classe revisada</th><th>Revisão</th><th>QGC</th></tr></thead>
                    <tbody>
                        <tr v-for="credit in request.credits" :key="credit.id">
                            <td>{{ creditorName(credit.credit_certificate_creditor_id) }}</td>
                            <td>{{ credit.company_name || '—' }}</td>
                            <td>{{ credit.origin || '—' }}</td>
                            <td class="text-right chc-nowrap">{{ currencySymbol(credit.currency) }} {{ formatMoney(credit.amount) }}</td>
                            <td>{{ className(credit.informed_class) }}</td>
                            <td>
                                <span v-if="credit.reviewed_class" :class="{ 'chc-warn': Number(credit.reviewed_class) !== Number(credit.informed_class) }">{{ className(credit.reviewed_class) }}</span>
                                <span v-else class="ui-muted">—</span>
                            </td>
                            <td class="chc-pre">{{ credit.review_note || '—' }}</td>
                            <td><ui-badge v-if="credit.user_creditor_id" text="Lançado" tone="success" /><span v-else class="ui-muted">—</span></td>
                        </tr>
                    </tbody>
                </v-simple-table>
                <!-- Revisao: um bloco por credito -->
                <div v-else>
                    <div v-for="(credit, index) in form.credits" :key="credit.key" class="chc-credit">
                        <v-row dense>
                            <v-col cols="12" md="4"><v-select v-model="credit.creditor_key" :items="creditorOptions" item-value="key" item-text="name" label="Credor" dense hide-details outlined /></v-col>
                            <v-col cols="12" md="4"><v-select v-model="credit.client_company_id" :items="companyOptions" item-value="id" item-text="name" label="Devedora" dense hide-details outlined /></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="credit.origin" label="Origem" dense hide-details outlined /></v-col>
                            <v-col cols="4" md="2"><v-select v-model="credit.currency" :items="currencies" item-value="id" item-text="name" label="Moeda" dense hide-details outlined /></v-col>
                            <v-col cols="8" md="2"><v-currency-field v-model="credit.amount" :allowNegative="false" label="Valor" dense hide-details outlined /></v-col>
                            <v-col cols="12" md="4"><v-select v-model="credit.informed_class" :items="classes" item-value="id" item-text="name" label="Classe informada" dense hide-details outlined /></v-col>
                            <v-col cols="12" md="4"><v-select v-model="credit.reviewed_class" :items="classes" item-value="id" item-text="name" clearable label="Classe revisada" placeholder="Igual à informada" persistent-placeholder dense hide-details outlined /></v-col>
                            <v-col cols="11"><v-text-field v-model="credit.review_note" label="Motivo da revisão (interno)" dense hide-details outlined /></v-col>
                            <v-col cols="1" class="d-flex align-center justify-end">
                                <v-btn icon small :disabled="form.credits.length === 1" title="Remover crédito" @click="form.credits.splice(index, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                            </v-col>
                        </v-row>
                    </div>
                </div>
            </ui-section>

            <!-- Honorarios -->
            <ui-section v-if="request.fees.length || editing" title="Honorários">
                <template v-if="editing" #actions>
                    <v-btn outlined small color="primary" class="mr-2" @click="addFee('attorney')"><v-icon left small>mdi-plus</v-icon>Advocatícios</v-btn>
                    <v-btn outlined small color="primary" @click="addFee('expert')"><v-icon left small>mdi-plus</v-icon>Periciais</v-btn>
                </template>
                <ui-empty v-if="editing && !form.fees.length" icon="mdi-scale-balance" text="Sem honorários." />
                <v-simple-table v-else dense>
                    <thead><tr><th>Tipo</th><th>Nome</th><th>CPF/CNPJ</th><th>OAB</th><th>Fixado em</th><th class="text-right">Valor (R$)</th><th v-if="editing"></th></tr></thead>
                    <tbody v-if="!editing">
                        <tr v-for="fee in request.fees" :key="fee.id">
                            <td>{{ fee.kind === 'attorney' ? 'Advocatícios' : 'Periciais' }}</td>
                            <td>{{ fee.name }}</td>
                            <td>{{ formatRegistration(fee.registration) || '—' }}</td>
                            <td>{{ fee.oab ? `${fee.oab}${fee.oab_state ? '/' + fee.oab_state : ''}` : '—' }}</td>
                            <td>{{ formatDate(fee.fixed_at) || '—' }}</td>
                            <td class="text-right">{{ formatMoney(fee.amount) }}</td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-for="(fee, index) in form.fees" :key="index">
                            <td>{{ fee.kind === 'attorney' ? 'Advocatícios' : 'Periciais' }}</td>
                            <td><v-text-field v-model="fee.name" dense hide-details outlined class="chc-input" /></td>
                            <td><v-text-field v-model="fee.registration" dense hide-details outlined class="chc-input" /></td>
                            <td>
                                <div v-if="fee.kind === 'attorney'" class="d-flex">
                                    <v-text-field v-model="fee.oab" dense hide-details outlined class="chc-input mr-1" />
                                    <v-select v-model="fee.oab_state" :items="states" dense hide-details outlined class="chc-input chc-input--uf" />
                                </div>
                                <span v-else class="ui-muted">—</span>
                            </td>
                            <td><v-text-field v-if="fee.kind === 'expert'" v-model="fee.fixed_at" type="date" dense hide-details outlined class="chc-input" /><span v-else class="ui-muted">—</span></td>
                            <td><v-currency-field v-model="fee.amount" :allowNegative="false" dense hide-details outlined class="chc-input chc-input--money" /></td>
                            <td class="text-right"><v-btn icon x-small @click="form.fees.splice(index, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn></td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </ui-section>

            <!-- Documentos -->
            <ui-section title="Documentos">
                <ui-empty v-if="!request.documents.length" icon="mdi-file-outline" text="Nenhum documento." />
                <v-simple-table v-else dense>
                    <thead><tr><th>Documento</th><th>Arquivo</th><th>Enviado em</th><th></th></tr></thead>
                    <tbody>
                        <tr v-for="doc in request.documents" :key="doc.id">
                            <td>{{ nameOf(documentKinds, doc.kind) }}</td>
                            <td>{{ doc.original_name || '—' }}</td>
                            <td>{{ formatDateTime(doc.created_at) }}</td>
                            <td class="text-right">
                                <v-btn text small color="primary" :loading="openingDoc === doc.id" @click="openDocument(doc)"><v-icon left small>mdi-open-in-new</v-icon>Abrir</v-btn>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </ui-section>

            <!-- Decisao -->
            <ui-section v-if="!editing" title="Decisão" :description="decisionHint">
                <div class="chc-decision">
                    <template v-if="isOpen">
                        <v-btn v-if="$allowed('event.chc_decide')" color="success" depressed :loading="deciding === 'approve'" :disabled="!!deciding" @click="approve">
                            <v-icon left small>mdi-check-circle-outline</v-icon>Aprovar e lançar no QGC
                        </v-btn>
                        <v-btn v-if="$allowed('event.chc_decide')" outlined color="error" :disabled="!!deciding" @click="openDialog('reject')">
                            <v-icon left small>mdi-close-circle-outline</v-icon>Rejeitar
                        </v-btn>
                        <v-btn v-if="$allowed('create')" outlined color="primary" :disabled="!!deciding" @click="openDialog('documents')">
                            <v-icon left small>mdi-file-send-outline</v-icon>Solicitar novos documentos
                        </v-btn>
                        <v-btn v-if="$allowed('event.chc_decide')" outlined color="grey darken-1" :disabled="!!deciding" @click="openDialog('outdated')">
                            <v-icon left small>mdi-calendar-alert</v-icon>Certidão desatualizada
                        </v-btn>
                    </template>
                    <v-btn v-if="canRestore && $allowed('event.chc_decide')" outlined color="primary" :loading="deciding === 'restore'" :disabled="!!deciding" @click="restore">
                        <v-icon left small>mdi-restore</v-icon>Restaurar para pendente
                    </v-btn>
                </div>
            </ui-section>
        </template>

        <!-- Fundamentacao / pedido de documentos -->
        <v-dialog v-model="dialog.show" max-width="620" persistent>
            <v-card>
                <v-card-title>{{ dialogTitle }}</v-card-title>
                <v-card-text>
                    <template v-if="dialog.type === 'documents'">
                        <p class="mb-2">O requerente vê o pedido na área do credor e envia os documentos por lá.</p>
                        <v-checkbox v-for="kind in requestableDocuments" :key="kind.id" v-model="dialog.documents" :value="kind.id" :label="kind.label" dense hide-details class="mt-0 mb-1" />
                    </template>
                    <v-textarea v-model="dialog.text" :label="dialog.type === 'documents' ? 'Orientações ao requerente (opcional)' : 'Fundamentação *'" rows="4" auto-grow outlined hide-details="auto" class="mt-3" />
                    <p class="ui-muted mt-2 mb-0">{{ dialog.type === 'documents' ? 'As orientações aparecem para o requerente.' : 'A fundamentação aparece para o requerente na área do credor.' }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="!!deciding" @click="dialog.show = false">Cancelar</v-btn>
                    <v-btn :color="dialog.type === 'reject' ? 'error' : 'primary'" depressed :loading="!!deciding" :disabled="!dialogValid" @click="confirmDialog">Confirmar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import { displayDate } from '@/common/util/date-time';
import statesList from "@/common/data/states-list.data";
import currencyTypes from "@/common/data/currency-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import { statuses, justiceBranches, requesterTypes, documentKinds, nameOf, toneOf } from "@/common/data/credit-certificate.data";

let keySeed = 0;

// Analise de uma CHC (Certidao de Habilitacao de Credito): revisar dados e
// classe, pedir documentos, aprovar (lanca no QGC vigente) ou rejeitar.
export default {
    name: "CreditCertificateDetails",

    data: () => ({
        statuses,
        justiceBranches,
        requesterTypes,
        documentKinds,
        currencies: currencyTypes,
        states: statesList.map((state) => state.name || state),
        loading: false,
        saving: false,
        deciding: null,
        openingDoc: null,
        editing: false,
        request: {},
        companies: [],
        classes: [],
        form: null,
        dialog: { show: false, type: null, text: '', documents: [] },
        requestableDocuments: [
            { id: 'chc', label: 'Nova versão da CHC' },
            { id: 'full_process', label: 'Processo integral' },
        ],
    }),

    computed: {
        isOpen() {
            return ['pending', 'in_analysis', 'documents_requested', 'documents_updated'].includes(this.request.status);
        },
        canRestore() {
            return ['rejected', 'outdated', 'documents_requested'].includes(this.request.status);
        },
        totalBrl() {
            return (this.request.credits || []).filter((credit) => Number(credit.currency) === 1).reduce((sum, credit) => sum + Number(credit.amount || 0), 0);
        },
        decisionHint() {
            if (this.request.status === 'approved') return 'CHC aprovada: os créditos já estão no QGC. Ajustes passam a ser feitos no QGC.';
            if (!this.isOpen) return 'Restaure para pendente para reabrir a análise.';
            return 'Aprovar lança cada crédito no QGC vigente do caso, com a classe revisada (ou a informada), e registra no histórico.';
        },
        creditorOptions() {
            return (this.form ? this.form.creditors : []).map((creditor, index) => ({ key: creditor.key, name: creditor.name || `Credor ${index + 1}` }));
        },
        companyOptions() {
            return [{ id: null, name: this.request.client_name }, ...this.companies.map((company) => ({ id: company.id, name: company.name }))];
        },
        dialogTitle() {
            return { reject: 'Rejeitar CHC', outdated: 'Certidão desatualizada', documents: 'Solicitar novos documentos' }[this.dialog.type] || '';
        },
        dialogValid() {
            if (this.dialog.type === 'documents') return this.dialog.documents.length > 0;
            return !!this.dialog.text.trim();
        },
    },

    created() {
        this.load();
    },

    methods: {
        nameOf,
        toneOf,

        newKey() {
            keySeed += 1;
            return `n${keySeed}`;
        },

        formatDate(value) {
            return value ? this.$moment(String(value).slice(0, 10)).format('DD/MM/YYYY') : '';
        },

        formatDateTime(value) {
            // Data e hora gravadas pela API (UTC): exibidas no horario de Brasilia.
            return value ? displayDate(value).format('DD/MM/YYYY HH:mm') : '';
        },

        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatRegistration(value) {
            const digits = String(value || '').replace(/\D/g, '');
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value || '';
        },

        currencySymbol(id) {
            const currency = currencyTypes.find((item) => Number(item.id) === Number(id));
            return currency ? currency.currencyFormat : '';
        },

        className(id) {
            const item = this.classes.find((entry) => Number(entry.id) === Number(id));
            return item ? item.name : (id ? `Classe ${id}` : '—');
        },

        creditorName(id) {
            const creditor = (this.request.creditors || []).find((item) => Number(item.id) === Number(id));
            return creditor ? creditor.name : '—';
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/user-events/credit-certificates/${this.$route.params.id}`);
                this.request = result.data.content;
                this.editing = false;
                await this.loadCaseData();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async loadCaseData() {
            this.classes = await creditorClassUtil.listByClient(this.request.client_id, { includeInactive: true });
            try {
                const result = await axios.get(`/api/clients/${this.request.client_id}`);
                const client = result.data.content || {};
                this.companies = Number(client.consolidation_type) === 2 ? [] : (client.companies || []);
            } catch (error) {
                this.companies = [];
            }
        },

        startEditing() {
            const r = this.request;
            this.form = {
                process_number: r.process_number,
                process_state: r.process_state,
                justice_branch: r.justice_branch,
                court: r.court,
                final_judgment_at: r.final_judgment_at ? String(r.final_judgment_at).slice(0, 10) : '',
                calculation_updated_at: r.calculation_updated_at ? String(r.calculation_updated_at).slice(0, 10) : '',
                notes: r.notes,
                confidential: !!r.confidential,
                creditors: r.creditors.map((creditor) => ({ key: `c${creditor.id}`, name: creditor.name, registration: creditor.registration })),
                credits: r.credits.map((credit) => ({
                    key: `r${credit.id}`, id: credit.id, creditor_key: `c${credit.credit_certificate_creditor_id}`, client_company_id: credit.client_company_id || null,
                    origin: credit.origin, currency: credit.currency, amount: null, informed_class: credit.informed_class,
                    reviewed_class: credit.reviewed_class || null, review_note: credit.review_note || '',
                })),
                fees: r.fees.map((fee) => ({ ...fee, amount: null, fixed_at: fee.fixed_at ? String(fee.fixed_at).slice(0, 10) : '' })),
            };
            this.editing = true;
            // O v-currency-field so formata valor que muda depois de montado.
            this.$nextTick(() => {
                this.form.credits.forEach((credit, index) => { credit.amount = Number(r.credits[index].amount); });
                this.form.fees.forEach((fee, index) => { fee.amount = Number(r.fees[index].amount); });
            });
        },

        cancelEditing() {
            this.editing = false;
            this.form = null;
        },

        removeCreditor(index) {
            const [removed] = this.form.creditors.splice(index, 1);
            this.form.credits.forEach((credit) => { if (credit.creditor_key === removed.key) credit.creditor_key = this.form.creditors[0].key; });
        },

        addCredit() {
            this.form.credits.push({ key: this.newKey(), creditor_key: this.form.creditors[0].key, client_company_id: null, origin: '', currency: 1, amount: 0, informed_class: 1, reviewed_class: null, review_note: '' });
        },

        addFee(kind) {
            this.form.fees.push({ kind, name: '', registration: '', oab: '', oab_state: null, amount: 0, fixed_at: '' });
        },

        async saveReview() {
            const form = this.form;
            this.saving = true;
            try {
                const result = await axios.patch(`/api/admin/user-events/credit-certificates/${this.request.id}`, {
                    ...form,
                    has_attorney_fees: form.fees.some((fee) => fee.kind === 'attorney'),
                    has_expert_fees: form.fees.some((fee) => fee.kind === 'expert'),
                });
                this.request = result.data.content;
                this.editing = false;
                this.form = null;
                this.$dialog.notify.success('Revisão salva.', { position: 'top-right', timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.saving = false;
            }
        },

        async openDocument(doc) {
            this.openingDoc = doc.id;
            try {
                const result = await axios.get(`/api/admin/user-events/credit-certificates/${this.request.id}/documents/${doc.id}`);
                window.open(result.data.content.url, '_blank', 'noopener');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.openingDoc = null;
            }
        },

        async decide(decision, reason = null) {
            this.deciding = decision;
            try {
                const result = await axios.post(`/api/admin/user-events/credit-certificates/${this.request.id}/decision`, { decision, reason });
                this.request = result.data.content;
                return true;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
                return false;
            } finally {
                this.deciding = null;
            }
        },

        async approve() {
            const duplicated = this.request.creditors.filter((creditor) => creditor.qgc_claims && creditor.qgc_claims.length);
            const lines = this.request.credits.map((credit) => `• ${this.creditorName(credit.credit_certificate_creditor_id)}: ${this.className(credit.reviewed_class || credit.informed_class)} · ${this.currencySymbol(credit.currency)} ${this.formatMoney(credit.amount)}`);
            const proceed = await this.$dialog.confirm({
                title: 'Aprovar CHC',
                text: `Os créditos abaixo serão lançados no QGC vigente${this.request.qgc_version ? ` (versão ${this.request.qgc_version})` : ''}:<br>${lines.join('<br>')}`
                    + (duplicated.length ? `<br><br><strong>Atenção:</strong> ${duplicated.map((creditor) => creditor.name).join(', ')} já tem crédito no QGC.` : ''),
                actions: { false: 'Cancelar', true: 'Aprovar' },
            });
            if (!proceed) return;
            if (await this.decide('approve')) this.$dialog.notify.success('CHC aprovada e créditos lançados no QGC.', { position: 'top-right', timeout: 4000 });
        },

        async restore() {
            if (await this.decide('restore')) this.$dialog.notify.success('CHC restaurada para pendente.', { position: 'top-right', timeout: 3000 });
        },

        openDialog(type) {
            this.dialog = { show: true, type, text: '', documents: type === 'documents' ? ['chc'] : [] };
        },

        async confirmDialog() {
            const { type, text, documents } = this.dialog;
            if (type === 'documents') {
                this.deciding = 'documents';
                try {
                    const result = await axios.post(`/api/admin/user-events/credit-certificates/${this.request.id}/request-documents`, { documents, message: text });
                    this.request = result.data.content;
                    this.dialog.show = false;
                    this.$dialog.notify.success('Documentos solicitados ao requerente.', { position: 'top-right', timeout: 3000 });
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
                } finally {
                    this.deciding = null;
                }
                return;
            }
            if (await this.decide(type === 'reject' ? 'reject' : 'outdated', text.trim())) {
                this.dialog.show = false;
                this.$dialog.notify.success(type === 'reject' ? 'CHC rejeitada.' : 'CHC marcada como desatualizada.', { position: 'top-right', timeout: 3000 });
            }
        },
    },
};
</script>

<style scoped>
.chc-overview { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; margin-bottom: 18px; }
.chc-overview__item { display: flex; flex-direction: column; padding: 12px 14px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; }
.chc-label { display: block; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; margin-bottom: 2px; }
.chc-value { font-size: 14px; font-weight: 600; color: #101828; }
.chc-sub { font-size: 12.5px; color: #667085; margin-top: 2px; }
.chc-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px 24px; font-size: 13.5px; }
.chc-grid > div { display: flex; flex-direction: column; }
.chc-grid__wide { grid-column: 1 / -1; }
.chc-pre { white-space: pre-wrap; }
.chc-nowrap { white-space: nowrap; }
.chc-warn { color: #b54708; font-weight: 600; }
.chc-decision { display: flex; flex-wrap: wrap; gap: 10px; }
.chc-input { min-width: 120px; font-size: 13px; }
.chc-input--money { min-width: 130px; }
.chc-input--uf { min-width: 80px; max-width: 90px; }
.chc-table td { vertical-align: middle; }
.chc-credit { padding: 10px 0; border-top: 1px solid #eaecf0; }
.chc-credit:first-child { border-top: 0; padding-top: 0; }
.chc-table ::v-deep td { padding-top: 4px !important; padding-bottom: 4px !important; }
</style>
